import { QueryFactory } from '@lmt/shared-types';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { downloadAndOpenFile } from '@lmt/unicorn-shared/app/helpers/download-files';
import { triggerDownloadFileDialog } from '@lmt/unicorn-shared/app/helpers/file-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../common/ui-kit/buttons/Button';
import { FormComponentProps } from '../../global-forms/GlobalForms.types';
export type Props = FormComponentProps & {
  processId: string;
};
import styles from './ReImportMeasurementsForm.module.scss';

export const ReImportMeasurementsForm: React.FC<Props> = React.memo(
  function ReImportMeasurementsForm(props) {
    const i18next = useScopedTranslation('mappings.re_import_measurements');
    const dispatch = useUniDispatch();
    const modals = useModal();
    const [isLoading, setLoading] = useState<boolean>(false);
    const reImportResultQuery =
      QueryFactory.DataBlockQuery.useGetReimportResultQuery(props.processId, {
        refetchInterval: 1000,
      });
    const isFinished = reImportResultQuery.data?.isFinished;
    const errors = reImportResultQuery.data?.errors;

    const onOpenFile = useCallback(async () => {
      if (!errors || !errors.length) {
        return;
      }
      await downloadAndOpenFile(getFileName(), getBlob(errors));
    }, [errors]);

    const onDownloadFile = useCallback(async () => {
      if (!errors || !errors.length) {
        return;
      }
      setLoading(true);
      try {
        triggerDownloadFileDialog(getBlob(errors), getFileName());
      } finally {
        setLoading(false);
      }
    }, [dispatch, errors]);

    const onClick = isElectron() ? onOpenFile : onDownloadFile;

    const onCopy = useCallback(async () => {
      if (!errors || !errors.length) {
        return;
      }
      await navigator.clipboard.writeText(errors.join('\n'));
    }, [errors]);

    const statusMessage = useMemo(() => {
      if (!isFinished) {
        return i18next.t('re_import_starts');
      } else {
        if (!errors || !errors.length) {
          return i18next.t('re_import_successfully_finished');
        } else {
          return i18next.t('re_import_finished_with_errors');
        }
      }
    }, [isFinished, errors]);

    useEffect(() => {
      props.setIsCloseButtonHidden(props.formId, !isFinished);
    }, [isFinished]);

    return (
      <div className={styles.container}>
        <div className={styles.dataCon}>
          <div className={styles.header}>{statusMessage}</div>
          {errors && errors.length > 0 && (
            <div className={styles.errorList}>
              {reImportResultQuery.data?.errors?.map((error) => (
                <div className={styles.error}>{error}</div>
              ))}
            </div>
          )}
        </div>
        {isFinished && errors && errors.length > 0 && (
          <div className={styles.controls}>
            <Button
              disabled={!reImportResultQuery.data?.isFinished}
              title={i18next.t('copy_button')}
              onClick={onCopy}
              className={styles.button}
            />
            <Button
              disabled={isLoading}
              title={i18next.t('download_button')}
              onClick={onClick}
              className={styles.button}
            />
          </div>
        )}
      </div>
    );
  },
);

function getFileName(): string {
  const date = format(new Date(), 'yyyyMMddHHmmss');
  return date + '_reimport_logs.txt';
}

function getBlob(errors: string[]) {
  const content = errors.join('\n');
  return new Blob([content], { type: 'text/plain' });
}
